/**
 * カテゴリの木（最大3階層・素材は末端にのみ）を扱う純関数。
 *
 * 不変条件の本体は DB トリガ（20260823010000）にある。ここは画面の出し分け
 * （どこに子を足せるか・どこへ移せるか・どのカテゴリを選べるか）のための計算で、
 * 担保ではない。依存なし・副作用なしにしてテストで固定する（roles.ts と同じ流儀）。
 *
 * 木は小さい（数十件）前提。毎回線形に探しても体感できる差は出ないので、
 * 索引構造で複雑にしない。
 */
import type { Category } from './supabase.ts'

/** DB の app.category_max_depth() と一致させる。 */
export const CATEGORY_MAX_DEPTH = 3

export type CategoryNode = Category & {
  /** 最上位 = 1。 */
  depth: number
  children: CategoryNode[]
}

/** 兄弟の並び: sort_order → 名前。 */
const bySiblingOrder = (a: Category, b: Category) =>
  a.sort_order - b.sort_order || a.name.localeCompare(b.name, 'ja')

/**
 * 平坦な一覧から木を組む。親が一覧に無いカテゴリは最上位として扱う
 * （表示から消えるより、見えて気づける方がよい）。
 */
export function buildCategoryTree(categories: Category[]): CategoryNode[] {
  const ids = new Set(categories.map((c) => c.id))
  const build = (parentId: string | null, depth: number, seen: Set<string>): CategoryNode[] =>
    categories
      .filter((c) => (parentId === null ? c.parent_id === null || !ids.has(c.parent_id) : c.parent_id === parentId))
      .filter((c) => !seen.has(c.id))
      .sort(bySiblingOrder)
      .map((c) => {
        const next = new Set(seen)
        next.add(c.id)
        return { ...c, depth, children: build(c.id, depth + 1, next) }
      })
  return build(null, 1, new Set())
}

/** 木を深さ優先で平坦にする（表示順）。 */
export function flattenTree(nodes: CategoryNode[]): CategoryNode[] {
  const out: CategoryNode[] = []
  const walk = (list: CategoryNode[]) => {
    for (const n of list) {
      out.push(n)
      walk(n.children)
    }
  }
  walk(nodes)
  return out
}

/** 最上位から id までの経路。未知の id なら空。 */
export function categoryPath(categories: Category[], id: string | null): Category[] {
  const byId = new Map(categories.map((c) => [c.id, c]))
  const path: Category[] = []
  const seen = new Set<string>()
  let cur = id ? byId.get(id) : undefined
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id)
    path.unshift(cur)
    cur = cur.parent_id ? byId.get(cur.parent_id) : undefined
  }
  return path
}

/** 「鉄道 › 新幹線 › 500系」の形。未知なら '—'。 */
export function categoryPathLabel(categories: Category[], id: string | null, sep = ' › '): string {
  const path = categoryPath(categories, id)
  return path.length ? path.map((c) => c.name).join(sep) : '—'
}

/** 深さ（最上位 = 1）。未知なら 0。 */
export const categoryDepth = (categories: Category[], id: string | null): number =>
  categoryPath(categories, id).length

/** 自分を含む子孫の id。 */
export function descendantIds(categories: Category[], id: string): Set<string> {
  const out = new Set<string>([id])
  let frontier = [id]
  while (frontier.length) {
    const next: string[] = []
    for (const c of categories) {
      if (c.parent_id && frontier.includes(c.parent_id) && !out.has(c.id)) {
        out.add(c.id)
        next.push(c.id)
      }
    }
    frontier = next
  }
  return out
}

export const hasChildren = (categories: Category[], id: string): boolean =>
  categories.some((c) => c.parent_id === id)

/** 末端（子を持たない）か。素材を置けるのはここだけ。 */
export const isLeafCategory = (categories: Category[], id: string): boolean => !hasChildren(categories, id)

/** 部分木の高さ（自分だけなら 1）。 */
export function subtreeHeight(categories: Category[], id: string): number {
  const children = categories.filter((c) => c.parent_id === id)
  if (children.length === 0) return 1
  return 1 + Math.max(...children.map((c) => subtreeHeight(categories, c.id)))
}

/** 素材を置ける（末端の）カテゴリを、経路ラベル順で。 */
export function leafCategories(categories: Category[]): Category[] {
  return flattenTree(buildCategoryTree(categories)).filter((c) => c.children.length === 0)
}

/**
 * id の下に子カテゴリを足せるか。深さの上限と「素材を持つカテゴリに子は作れない」の両方。
 * 素材の有無は呼び出し側が数えて渡す（画面に出ている素材で十分。担保は DB）。
 */
export const canAddChild = (categories: Category[], id: string, assetCount: number): boolean =>
  categoryDepth(categories, id) < CATEGORY_MAX_DEPTH && assetCount === 0

/**
 * id を移せる親の候補。自分と自分の配下は除き、部分木の高さが上限に収まる先だけ。
 * 「移動先に素材があるか」はここでは見ない（サーバが 409 category_parent_has_assets で断る）。
 * 最上位（null）へ移せるかは常に真（高さは既に上限内）。
 */
export function eligibleParents(categories: Category[], id: string): Category[] {
  const blocked = descendantIds(categories, id)
  const height = subtreeHeight(categories, id)
  return flattenTree(buildCategoryTree(categories)).filter(
    (c) => !blocked.has(c.id) && c.depth + height <= CATEGORY_MAX_DEPTH,
  )
}
